import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { motion } from 'framer-motion';
import { Upload, Save, RotateCcw, Eye, Check, AlertCircle } from 'lucide-react';

const DEFAULT_CUSTOMER_REVIEWS = [
  {
    quote: 'Finally found a brand that truly understands fit and style for bigger sizes. Amazing quality and super comfortable.',
    name: 'Rohit S.',
  },
  {
    quote: 'The fabric, the fit, the design - everything is on point. BeyondXL is now my go-to shirts brand.',
    name: 'Amit Verma',
  },
  {
    quote: 'Great collections and fast delivery. Love the way these shirts make me look confident.',
    name: 'Karan P.',
  },
];

const normalizeReviews = (reviews) =>
  [0, 1, 2].map((index) => ({
    quote: reviews?.[index]?.quote || '',
    name: reviews?.[index]?.name || '',
  }));

const LandingPageSettings = () => {
  const [settings, setSettings] = useState({
    background_image: '',
    hero_title: '',
    hero_subtitle: '',
    hero_description: '',
    story_label: '',
    story_heading: '',
    story_paragraph_1: '',
    story_paragraph_2: '',
    story_paragraph_3: '',
    story_image: '',
    reviews_heading: '',
    customer_reviews: normalizeReviews(DEFAULT_CUSTOMER_REVIEWS),
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingStory, setUploadingStory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getLandingPageSettings();
      if (response.success) {
        setSettings({
          ...response.settings,
          customer_reviews: normalizeReviews(response.settings.customer_reviews),
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (PNG, JPG, JPEG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setMessage('');

      const formData = new FormData();
      formData.append('image', file);

      const response = await adminAPI.uploadBackgroundImage(formData);
      if (response.data.success) {
        setSettings(prev => ({
          ...prev,
          background_image: response.data.image_url
        }));
        setMessage('Background image uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleStoryImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (PNG, JPG, JPEG, GIF, or WebP)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingStory(true);
      setError('');
      setMessage('');

      const formData = new FormData();
      formData.append('image', file);

      const response = await adminAPI.uploadStoryImage(formData);
      if (response.data.success) {
        setSettings(prev => ({
          ...prev,
          story_image: response.data.image_url
        }));
        setMessage('Our Story image uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading story image:', error);
      setError(error.message || 'Failed to upload story image');
    } finally {
      setUploadingStory(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');

      const response = await adminAPI.updateLandingPageSettings(settings);
      if (response.success) {
        setSettings({
          ...response.settings,
          customer_reviews: normalizeReviews(response.settings.customer_reviews),
        });
        setMessage('Settings saved successfully!');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');

      const response = await adminAPI.resetLandingPageSettings();
      if (response.success) {
        setSettings({
          ...response.settings,
          customer_reviews: normalizeReviews(response.settings.customer_reviews),
        });
        setMessage('Settings reset to defaults successfully!');
      }
    } catch (error) {
      console.error('Error resetting settings:', error);
      setError(error.message || 'Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReviewChange = (index, field, value) => {
    setSettings((prev) => ({
      ...prev,
      customer_reviews: prev.customer_reviews.map((review, reviewIndex) =>
        reviewIndex === index ? { ...review, [field]: value } : review
      ),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Landing Page Settings</h1>
          <p className="text-gray-600">Manage your homepage background and hero content</p>
        </div>
        <motion.button
          onClick={() => setPreviewMode(!previewMode)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Eye size={20} />
          {previewMode ? 'Hide Preview' : 'Show Preview'}
        </motion.button>
      </div>

      {/* Messages */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg"
        >
          <Check className="text-green-600" size={20} />
          <span className="text-green-800">{message}</span>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg"
        >
          <AlertCircle className="text-red-600" size={20} />
          <span className="text-red-800">{error}</span>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Form */}
        <div className="space-y-6">
          {/* Background Image Upload */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-lg border border-gray-200"
          >
            <h3 className="text-lg font-semibold mb-4">Background Image</h3>
            
            {/* Current Image Preview */}
            {settings.background_image && (
              <div className="mb-4">
                <img
                  src={settings.background_image}
                  alt="Current background"
                  className="w-full h-32 object-cover rounded-lg border border-gray-200"
                />
              </div>
            )}

            {/* Upload Button */}
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
              />
              <motion.div
                className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer transition-colors ${
                  uploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-indigo-500'
                }`}
                whileHover={!uploading ? { scale: 1.02 } : {}}
                whileTap={!uploading ? { scale: 0.98 } : {}}
              >
                <Upload size={20} />
                <span>{uploading ? 'Uploading...' : 'Upload New Background Image'}</span>
              </motion.div>
            </div>
            
            <p className="text-sm text-gray-500 mt-2">
              Recommended size: 1920x1080px. Max file size: 10MB
            </p>
          </motion.div>

          {/* Hero Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-lg border border-gray-200"
          >
            <h3 className="text-lg font-semibold mb-4">Hero Content</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hero Title
                </label>
                <input
                  type="text"
                  value={settings.hero_title}
                  onChange={(e) => handleInputChange('hero_title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter hero title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hero Subtitle
                </label>
                <input
                  type="text"
                  value={settings.hero_subtitle}
                  onChange={(e) => handleInputChange('hero_subtitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter hero subtitle"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hero Description
                </label>
                <textarea
                  value={settings.hero_description}
                  onChange={(e) => handleInputChange('hero_description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter hero description"
                />
              </div>
            </div>
          </motion.div>

          {/* Our Story */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white p-6 rounded-lg border border-gray-200"
          >
            <h3 className="text-lg font-semibold mb-4">Our Story Section</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Label
                </label>
                <input
                  type="text"
                  value={settings.story_label || ''}
                  onChange={(e) => handleInputChange('story_label', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Our Story"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heading
                </label>
                <input
                  type="text"
                  value={settings.story_heading || ''}
                  onChange={(e) => handleInputChange('story_heading', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Made for Every Plus Size..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paragraph 1
                </label>
                <textarea
                  value={settings.story_paragraph_1 || ''}
                  onChange={(e) => handleInputChange('story_paragraph_1', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paragraph 2
                </label>
                <textarea
                  value={settings.story_paragraph_2 || ''}
                  onChange={(e) => handleInputChange('story_paragraph_2', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paragraph 3
                </label>
                <textarea
                  value={settings.story_paragraph_3 || ''}
                  onChange={(e) => handleInputChange('story_paragraph_3', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {settings.story_image && (
              <div className="mt-4">
                <img
                  src={settings.story_image}
                  alt="Our Story"
                  className="w-full max-w-xs aspect-[4/5] object-cover rounded-lg border border-gray-200"
                />
              </div>
            )}

            <div className="relative mt-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleStoryImageUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploadingStory}
              />
              <motion.div
                className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer transition-colors ${
                  uploadingStory ? 'opacity-50 cursor-not-allowed' : 'hover:border-indigo-500'
                }`}
                whileHover={!uploadingStory ? { scale: 1.02 } : {}}
                whileTap={!uploadingStory ? { scale: 0.98 } : {}}
              >
                <Upload size={20} />
                <span>{uploadingStory ? 'Uploading...' : 'Upload Our Story Image'}</span>
              </motion.div>
            </div>

            <p className="text-sm text-gray-500 mt-2">
              Recommended size: 1200x1500px (4:5 ratio). Max file size: 10MB
            </p>
          </motion.div>

          {/* Customer Reviews */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 rounded-lg border border-gray-200"
          >
            <h3 className="text-lg font-semibold mb-4">What Our Customers Say</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Section Heading
              </label>
              <input
                type="text"
                value={settings.reviews_heading || ''}
                onChange={(e) => handleInputChange('reviews_heading', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="What our customers say"
              />
            </div>

            <div className="space-y-4">
              {settings.customer_reviews.map((review, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-800">Review {index + 1}</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quote
                    </label>
                    <textarea
                      value={review.quote}
                      onChange={(e) => handleReviewChange(index, 'quote', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Customer review quote"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Name
                    </label>
                    <input
                      type="text"
                      value={review.name}
                      onChange={(e) => handleReviewChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Customer name"
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex gap-3"
          >
            <motion.button
              onClick={handleSaveSettings}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              whileHover={!saving ? { scale: 1.02 } : {}}
              whileTap={!saving ? { scale: 0.98 } : {}}
            >
              <Save size={20} />
              {saving ? 'Saving...' : 'Save Settings'}
            </motion.button>

            <motion.button
              onClick={handleResetToDefaults}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              whileHover={!saving ? { scale: 1.02 } : {}}
              whileTap={!saving ? { scale: 0.98 } : {}}
            >
              <RotateCcw size={20} />
              Reset to Defaults
            </motion.button>
          </motion.div>
        </div>

        {/* Preview */}
        {previewMode && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white p-6 rounded-lg border border-gray-200"
          >
            <h3 className="text-lg font-semibold mb-4">Preview</h3>
            
            <div className="relative h-64 rounded-lg overflow-hidden">
              <img
                src={settings.background_image || 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920&h=1080&fit=crop'}
                alt="Background preview"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                <div className="text-center text-white">
                  <h2 className="text-2xl font-bold mb-2">{settings.hero_title || 'Fresh Fits, Fresh Feels'}</h2>
                  <p className="text-lg mb-2">{settings.hero_subtitle || 'Discover the latest in men\'s fashion'}</p>
                  <p className="text-sm opacity-90">{settings.hero_description || 'Premium quality, contemporary style.'}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default LandingPageSettings;
